// ============================================================
// receipt-print.js
// ------------------------------------------------------------
// เตรียมข้อมูลใบเสร็จแล้วเปิดหน้าเอกสาร — ใช้ร่วมกัน 4 หน้า
//   RecordOther (บันทึกรายรับอื่นๆ) · RecordReceipt (รับชำระ)
//   CapitalPaymentInfo (รับชำระเงินทุน) · UsageHistory (ประวัติค่าใช้จ่าย)
//
// ใบเสร็จมี 2 แบบ ระบบเลือกให้อัตโนมัติจากข้อมูลที่ดึงมา
//   - มี record_other_list  → ใบเสร็จรายรับอื่นๆ  (/doc/receipt-other)
//   - ไม่มี                 → ใบเสร็จค่าน้ำ        (/doc/receipt)
//
// ★ ใบเสร็จใบเดียวกัน พิมพ์จากหน้าไหนก็ต้องได้ยอดเท่ากันเป๊ะ
// เดิม 4 หน้านี้เขียนตรรกะแยกกันเอง แล้วคิดยอด "รวมทั้งสิ้น" ไม่เหมือนกัน
// ตอนนี้ยึดสูตรของหน้า record-receipt (หน้าที่ออกใบเสร็จตัวจริง) เป็นมาตรฐานเดียว
// ไม่มี option ให้เลือกอีกแล้ว — ทุกหน้าเรียก printReceiptByIwNumber() เหมือนกันหมด
//
// สูตรมาตรฐาน
//   - เรียงบิลตามเดือน (water_current_month) รายการแรก = งวดปัจจุบัน ที่เหลือ = ยอดค้างเดือนก่อน
//   - นับเฉพาะช่องที่ถูกเก็บเงินจริงในใบเสร็จนี้ (s_<ช่อง> = true)
//   - ลดหนี้หักออกจากยอดรวม (ไม่ใช่บวกเข้า)
//   - ค่าต่อกลับมิเตอร์รวมจากทุกงวด เพราะเกิดได้หลายงวดในใบเสร็จเดียว
//   - รวมทั้งสิ้น = ยอดค้างเดือนก่อน + ยอดงวดปัจจุบัน + เพิ่มหนี้ตอนรับชำระ - ลดหนี้ตอนรับชำระ
//     (เศษสตางค์ไม่รวมในยอดนี้ — ส่งแยกไปในช่อง change_satang)
// ============================================================

import { GET } from "../core/api.js";
import { tokens, getStorage } from "../core/storage.js";
import { alert, loading } from "../core/alert.js";
import { format_date, total, fieldSorter, roundToTwo } from "../core/format.js";
import { openDocument, projectAddressBlock } from "./document.js";

// ช่องค่าใช้จ่ายทั้งหมดที่แสดงบนใบเสร็จค่าน้ำ
const COST_TYPES = ["add_debt", "cable_cost", "common_expenses", "equipment_maintenance_fee", "fire_cost", "late_payment", "meter_connect_cost", "other_income", "reduce_debt", "waste_cost", "water_bill"];

// หัวกระดาษที่ใช้ร่วมกันทั้งใบเสร็จค่าน้ำและใบเสร็จรายรับอื่นๆ
const buildHeader = (data) => ({
  project_name: getStorage("project_name"),
  project_address: projectAddressBlock(),
  // NOTE cutomer_* สะกดผิดมาแต่เดิม — ส่งชื่อที่ถูก (customer_*) ควบคู่ไปด้วย
  // หน้าเอกสารอ่านชื่อที่ถูกก่อน เมื่อย้ายครบแล้วให้ลบคู่ที่สะกดผิดทิ้ง
  cutomer_name: data.full_name,
  cutomer_address: data.address || data.address_number,
  customer_name: data.full_name,
  customer_address: data.address || data.address_number,
  number: data.iw_number,
  date: format_date(data.record_date, "dd mmmm yyyy"),
  payment: data.payment_type === 1 ? "เงินสด" : "โอนเงินเข้าบัญชีนิติฯ",
  payment_date: `${format_date(data.transfer_date || data.record_date, "dd/mm/yyyy")} เวลา ${(data.transfer_time || data.record_time)?.slice(0, 5)}`,
});

// ข้อมูลใบเสร็จ "รายรับอื่นๆ"
const buildOtherReceipt = (data) => {
  const other = data.record_other_list?.[0];
  return {
    ...buildHeader(data),
    title: other?.title,
    description: other?.description,
    cost: other?.cost,
    total: other?.cost,
    change_satang: 0,
  };
};

// รายการบิลที่จะแสดงบนใบเสร็จ — เรียงตามเดือนของบิล
// ใบเสร็จเก่าบางใบไม่มี transaction_payments (ออกก่อนระบบมีตารางนี้) ให้ใช้ water_list แทน
const selectRows = (data) => {
  const rows = data.transaction_payments?.length ? data.transaction_payments : data.water_list || [];
  return [...rows].sort(fieldSorter(["water_current_month"]));
};

// รวมยอดที่ "เก็บเงินไปแล้ว" ของบิลกลุ่มหนึ่ง
// นับเฉพาะช่องที่มี s_<ช่อง> = true (คือช่องที่ถูกเก็บในการรับชำระครั้งนี้)
const sumPaid = (items) => {
  let sum = 0;
  COST_TYPES.forEach((type) => {
    const paid = items.filter((e) => e[`s_${type}`]);
    // ลดหนี้ต้องหักออก ไม่ใช่บวกเข้า
    if (type === "reduce_debt") sum -= total(paid, type);
    else sum += total(paid, type);
  });
  return roundToTwo(sum);
};

// ข้อมูลใบเสร็จ "ค่าน้ำ"
// รายการแรกคืองวดปัจจุบัน ที่เหลือคือยอดค้างเดือนก่อน ๆ
const buildWaterReceipt = (data) => {
  const rows = selectRows(data);
  const current = rows[0];

  // ยอดของช่องหนึ่งในงวดปัจจุบัน (ถ้าไม่ได้เก็บช่องนั้นให้เป็น 0)
  const currentAmount = (type) => (current?.[`s_${type}`] ? current[type] : 0);

  // ค่าใช้จ่ายของเดือนก่อนหน้าทั้งหมด (ไม่รวมงวดปัจจุบัน)
  const water_list_price = sumPaid(rows.slice(1));
  // รวมทั้งสิ้น = ยอดค้างเดือนก่อน + ยอดงวดปัจจุบัน + เพิ่มหนี้ตอนรับชำระ - ลดหนี้ตอนรับชำระ
  const total_calc = water_list_price + sumPaid(rows.slice(0, 1)) + data.add_on_add_debt - data.add_on_reduce_debt;

  const beforeLastDate = data.before_last_date ?? data.brefore_last_date;

  // เดือนก่อนหน้าของรอบบิลล่าสุด ใช้เป็นค่าสำรองถ้า backend ไม่ได้ส่งมา
  const lastMonth = new Date(data.last_date);
  lastMonth.setMonth(lastMonth.getMonth() - 1);

  return {
    ...buildHeader(data),
    water_current_month: data.last_date,
    // backend ส่งชื่อสะกดผิด (brefore_last_date) มาแต่เดิม ตอนนี้ส่งชื่อที่ถูก (before_last_date) มาด้วย
    // อ่านชื่อที่ถูกก่อน แล้วค่อย fallback ชื่อเดิม เพื่อรองรับ backend รุ่นเก่า
    water_last_month: beforeLastDate !== data.last_date ? beforeLastDate : format_date(lastMonth, "yyyy-mm-dd", "en"),
    last_gauge_number: data.last_gauge_number,
    current_gauge_number: data.current_gauge_number,
    unit_used: data.unit_used,
    water_bill: currentAmount("water_bill"),
    equipment_maintenance_fee: currentAmount("equipment_maintenance_fee"),
    waste_cost: currentAmount("waste_cost"),
    common_expenses: currentAmount("common_expenses"),
    water_list: rows || [],
    water_list_price,
    // ค่าต่อกลับมิเตอร์บนใบเสร็จ = งวดปัจจุบัน + ทุกงวดค้างที่มีการเก็บค่าต่อกลับมิเตอร์ (s_meter_connect_cost = true)
    // เอาไว้ "แสดงผลอย่างเดียว" — ห้ามเอาไปคำนวณยอดค้างชำระ/รวมทั้งสิ้น (คิดจาก sumPaid ของ rows ตรง ๆ อยู่แล้ว ไม่แตะ)
    // NOTE เดิมเคยลองรวมทุกงวดเข้าไปในยอด "รวมเป็นเงินทั้งสิ้น" ตรง ๆ แล้วพบว่าบรรทัดย่อยรวมเกินยอดจริง
    // (เพราะมิเตอร์งวดเก่านับซ้ำอยู่ใน water_list_price อยู่แล้ว) จึงแยกเป็นค่าตัวเลขแสดงผลต่างหากตัวนี้แทน
    meter_connect_cost: roundToTwo(currentAmount("meter_connect_cost") + total(rows.slice(1).filter((e) => e.s_meter_connect_cost), "meter_connect_cost")),
    late_payment: currentAmount("late_payment"),
    fire_cost: currentAmount("fire_cost"),
    cable_cost: currentAmount("cable_cost"),
    other_income: currentAmount("other_income"),
    add_debt: currentAmount("add_debt"),
    add_on_add_debt: data.add_on_add_debt,
    reduce_debt: currentAmount("reduce_debt"),
    add_on_reduce_debt: data.add_on_reduce_debt,
    total: total_calc,
    change_satang: data.change_satang,
  };
};

// ดึงข้อมูลใบเสร็จ 1 ใบตามเลขที่ใบแจ้งหนี้ (คืน null ถ้าไม่พบ)
const fetchReceipt = async (iw_number) => {
  const result = await GET(tokens, "v1/TH/record-receipt/history/by-iw-number/" + getStorage("project_id"), { iw_number });
  return result?.status ? result.data.data : null;
};

// พิมพ์ใบเสร็จหลายใบต่อกันในเอกสารเดียว — เรียงลงมาทีละใบ ขึ้นหน้าใหม่ทุกใบ
// ใช้จากหน้าปรับปรุงใบเสร็จรับเงิน ตอนออกใบทีเดียวหลายใบ
export const printReceiptsByIwNumbers = async (iw_numbers) => {
  if (!iw_numbers?.length) return;
  loading(true);
  try {
    const list = [];
    for (const iw_number of iw_numbers) {
      const data = await fetchReceipt(iw_number);
      if (!data) continue;
      list.push(data.record_other_list?.length ? buildOtherReceipt(data) : buildWaterReceipt(data));
    }
    if (!list.length) {
      alert("warning", "แจ้งเตือน", "ไม่มีใบแจ้งหนี้");
      return;
    }
    // หน้าเอกสารรับได้ทั้ง object เดียวและ array — ส่ง array เพื่อพิมพ์ต่อกันทีเดียว
    openDocument("/doc/receipt", list);
  } catch (e) {
    console.error(e);
    alert("warning", "แจ้งเตือน", "พิมพ์ใบเสร็จไม่สำเร็จ");
  } finally {
    loading(false);
  }
};

// ดึงข้อมูลใบเสร็จตามเลขที่ใบแจ้งหนี้ แล้วเปิดหน้าเอกสารในแท็บใหม่
//   onNotFound = callback เมื่อไม่พบใบแจ้งหนี้ (เช่นให้หน้าจอล้างฟอร์ม)
export const printReceiptByIwNumber = async (iw_number, onNotFound) => {
  loading(true);
  try {
    const result = await GET(tokens, "v1/TH/record-receipt/history/by-iw-number/" + getStorage("project_id"), { iw_number });
    if (!result?.status) {
      alert("warning", "แจ้งเตือน", "ไม่มีใบแจ้งหนี้");
      if (onNotFound) onNotFound();
      loading(false);
      return;
    }

    const data = result.data.data;
    if (data?.record_other_list.length !== 0) {
      openDocument("/doc/receipt-other", buildOtherReceipt(data));
    } else {
      openDocument("/doc/receipt", buildWaterReceipt(data));
    }
  } catch (e) {
    console.error(e);
    alert("warning", "แจ้งเตือน", "ไม่มีใบแจ้งหนี้");
  }
  loading(false);
};
